import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Group, Panel, Separator } from 'react-resizable-panels'
import { DisplayWindow, DisplayHeader, Display } from '../display'
import GlobalChat from '../chat/GlobalChat'
import PdfViewer from '../pdf/PdfViewer'
import { standardPdfUrl } from '../../services/pdfs'
import { standardId, standardName } from '../../utils/standardRef'
import './GlobalSearchDisplay.css'

const svgProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
}

const IconSearch = () => (
  <svg {...svgProps}>
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
)

// Double chevron for the pane collapse/expand toggles; `dir` points toward the
// direction the visible pane grows (right = grow chat, left = grow viewer).
const IconChevrons = ({ dir }) => (
  <svg {...svgProps}>
    {dir === 'right' ? (
      <>
        <polyline points="7 6 13 12 7 18" />
        <polyline points="13 6 19 12 13 18" />
      </>
    ) : (
      <>
        <polyline points="17 6 11 12 17 18" />
        <polyline points="11 6 5 12 11 18" />
      </>
    )}
  </svg>
)

// The inner chat | viewer split for one Global Search window: the whole-corpus
// chat on the left, a PDF viewer on the right. The viewer is empty for now (built
// out once results drive a selection). Split sizes persist per window via
// `onPanesChange` (stored on the grid window), mirroring SearchroomPanes so the
// two workspaces stay consistent. Each pane can be maximized from its header.
function GlobalSearchPanes({ chat, panes, onPanesChange }) {
  const defaultLayout = useMemo(
    () => ({ chat: panes?.chat ?? 45, viewer: panes?.viewer ?? 55 }),
    [panes]
  )

  const chatId = chat?._id
  const [searchOpen, setSearchOpen] = useState(false)
  // The reference the user last opened: { standard, page }. Drives the viewer,
  // which shows the full standard scrolled to the item's start page.
  const [viewerItem, setViewerItem] = useState(null)

  const openItem = useCallback((item) => {
    if (!item?.standard) return
    setViewerItem({ standard: item.standard, page: item.pageStart })
  }, [])

  const chatPanelRef = useRef(null)
  const viewerPanelRef = useRef(null)
  // Derived from onResize so it reflects manual drags and the persisted layout.
  const [collapsed, setCollapsed] = useState('none') // 'none' | 'chat' | 'viewer'

  const onChatResize = useCallback((size) => {
    setCollapsed((c) => (size.asPercentage <= 0.5 ? 'chat' : c === 'chat' ? 'none' : c))
  }, [])
  const onViewerResize = useCallback((size) => {
    setCollapsed((c) => (size.asPercentage <= 0.5 ? 'viewer' : c === 'viewer' ? 'none' : c))
  }, [])

  const toggleChatMaximize = () => {
    if (collapsed === 'viewer') viewerPanelRef.current?.expand()
    else viewerPanelRef.current?.collapse()
  }
  const toggleViewerMaximize = () => {
    if (collapsed === 'chat') chatPanelRef.current?.expand()
    else chatPanelRef.current?.collapse()
  }

  // Reset the search overlay and viewer selection when the window switches chats.
  useEffect(() => {
    setSearchOpen(false)
    setViewerItem(null)
  }, [chatId])

  return (
    <div className="gs-display">
      <Group
        orientation="horizontal"
        className="gs-panelgroup"
        defaultLayout={defaultLayout}
        onLayoutChanged={onPanesChange}
      >
        <Panel
          id="chat"
          className="gs-pane"
          defaultSize="45%"
          minSize="25%"
          collapsible
          collapsedSize="0%"
          panelRef={chatPanelRef}
          onResize={onChatResize}
        >
          <DisplayWindow
            header={
              <DisplayHeader
                title="Chat"
                actions={
                  <>
                    <button
                      type="button"
                      title={collapsed === 'viewer' ? 'Restore split view' : 'Expand chat pane'}
                      aria-label={
                        collapsed === 'viewer' ? 'Restore split view' : 'Expand chat pane'
                      }
                      className={collapsed === 'viewer' ? 'is-active' : ''}
                      onClick={toggleChatMaximize}
                    >
                      <IconChevrons dir={collapsed === 'viewer' ? 'left' : 'right'} />
                    </button>
                    <button
                      type="button"
                      title="Search messages"
                      aria-label="Search messages"
                      className={searchOpen ? 'is-active' : ''}
                      onClick={() => setSearchOpen((open) => !open)}
                    >
                      <IconSearch />
                    </button>
                  </>
                }
              />
            }
          >
            <Display fill>
              <GlobalChat
                chatId={chatId}
                searchOpen={searchOpen}
                onCloseSearch={() => setSearchOpen(false)}
                onOpenItem={openItem}
              />
            </Display>
          </DisplayWindow>
        </Panel>

        <Separator className="gs-resize-handle" />

        <Panel
          id="viewer"
          className="gs-pane"
          defaultSize="55%"
          minSize="25%"
          collapsible
          collapsedSize="0%"
          panelRef={viewerPanelRef}
          onResize={onViewerResize}
        >
          <DisplayWindow
            header={
              <DisplayHeader
                title={viewerItem ? standardName(viewerItem.standard) || 'Viewer' : 'Viewer'}
                actions={
                  <button
                    type="button"
                    title={collapsed === 'chat' ? 'Restore split view' : 'Expand viewer pane'}
                    aria-label={
                      collapsed === 'chat' ? 'Restore split view' : 'Expand viewer pane'
                    }
                    className={collapsed === 'chat' ? 'is-active' : ''}
                    onClick={toggleViewerMaximize}
                  >
                    <IconChevrons dir={collapsed === 'chat' ? 'right' : 'left'} />
                  </button>
                }
              />
            }
          >
            <Display fill>
              {viewerItem ? (
                <PdfViewer
                  key={standardId(viewerItem.standard)}
                  fileUrl={standardPdfUrl(viewerItem.standard)}
                  page={viewerItem.page}
                  standard={standardId(viewerItem.standard)}
                />
              ) : (
                <div className="display-empty">
                  Open a reference from an answer to view its standard here.
                </div>
              )}
            </Display>
          </DisplayWindow>
        </Panel>
      </Group>
    </div>
  )
}

export default GlobalSearchPanes
